import React, { useState, useEffect } from "react";
import { formatDate, formatAmount } from "../../utils/formatters";
import LoadingSpinner from "../common/LoadingSpinner";

const ContractChangesHistory = ({ contractId, contractService }) => {
  const [changes, setChanges] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchChanges = async () => {
      try {
        setLoading(true);
        const result = await contractService.getContractChanges(contractId);

        if (result.success) {
          setChanges(result.data);
        } else {
          console.error(
            "Échec de la récupération des modifications:",
            result.message
          );
          setError(
            "Échec de la récupération de l'historique des modifications."
          );
        }
      } catch (error) {
        console.error(
          "Erreur lors du chargement de l'historique des modifications:",
          error
        );
        setError(
          "Le service d'historique des modifications n'est pas encore disponible. Consultez la console pour plus de détails."
        );
      } finally {
        setLoading(false);
      }
    };

    fetchChanges();
  }, [contractId, contractService]);

  // Formater la valeur d'un champ en fonction de son type
  const formatFieldValue = (field, value) => {
    if (value === null || value === undefined) return "Non défini";

    // Formater les dates
    if (
      field.includes("Date") ||
      field === "timestamp" ||
      field === "createdAt" ||
      field === "updatedAt"
    ) {
      return formatDate(value, true);
    }

    // Formater les montants
    if (field.includes("Amount") || field === "discount") {
      return formatAmount(value);
    }

    // Formater les poids (nombre avec 2 décimales)
    if (field.includes("Weight")) {
      return Number(value).toFixed(2);
    }

    // Valeur par défaut (chaîne ou nombre)
    return String(value);
  };

  // Obtenir le libellé lisible pour un champ
  const getFieldLabel = (field) => {
    const fieldLabels = {
      contractNumber: "Numéro de contrat",
      agency: "Agence",
      jewelWeight: "Poids bijou",
      cdvaWeight: "Poids CDVA",
      cddWeight: "Poids CDD",
      status: "Statut",
      creationDate: "Date de création",
      limitDate: "Date limite",
      disbursementAmount: "Montant décaissé",
      expectedAmount: "Montant prévu",
      receivedAmount: "Montant perçu",
      remainingAmount: "Reste",
      lastPaymentDate: "Dernier versement",
      phone1: "Téléphone",
      phone2: "Ayant droit",
      client: "Client",
      closingDate: "Date de clôture",
      discount: "Remise",
      remark: "Remarque",
      etatContrat: "État du contrat",
      backgroundColor: "Couleur de fond",
      dossier: "Dossier",
    };

    return fieldLabels[field] || field;
  };

  if (loading) {
    return (
      <LoadingSpinner message="Chargement de l'historique des modifications..." />
    );
  }

  if (error) {
    return (
      <div className="bg-red-100 p-4 rounded-md text-red-700 mb-4">{error}</div>
    );
  }

  if (changes.length === 0) {
    return (
      <div className="bg-gray-50 p-4 rounded-md text-gray-500 italic text-center">
        <p>Aucun historique de modification disponible pour ce contrat.</p>
        <p className="mt-2 text-sm">Les modifications seront enregistrées à chaque mise à jour du contrat.</p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {changes.map((change, index) => (
        <div
          key={index}
          className="bg-gray-50 p-4 rounded-lg border border-gray-200"
        >
          <div className="flex justify-between items-center mb-2">
            <div className="font-medium">
              {change.importId ? (
                <span>
                  Import: {change.importId.fileName || "Import manuel"}
                  <span className="mx-1">•</span>
                  {formatDate(
                    change.importId.importDate || change.timestamp,
                    true
                  )}
                </span>
              ) : (
                <span>
                  Modification manuelle - {formatDate(change.timestamp, true)}
                </span>
              )}
            </div>
          </div>

          <div className="mt-2">
            <table className="min-w-full">
              <thead>
                <tr>
                  <th className="text-left text-xs font-medium text-gray-500 uppercase tracking-wider py-2">
                    Champ
                  </th>
                  <th className="text-left text-xs font-medium text-gray-500 uppercase tracking-wider py-2">
                    Ancienne valeur
                  </th>
                  <th className="text-left text-xs font-medium text-gray-500 uppercase tracking-wider py-2">
                    Nouvelle valeur
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {change.changes.map((fieldChange, fieldIndex) => (
                  <tr key={fieldIndex} className="hover:bg-gray-50">
                    <td className="py-2 text-sm font-medium text-gray-900">
                      {getFieldLabel(fieldChange.field)}
                    </td>
                    <td className="py-2 text-sm text-gray-500">
                      {formatFieldValue(
                        fieldChange.field,
                        fieldChange.oldValue
                      )}
                    </td>
                    <td className="py-2 text-sm text-gray-900">
                      {formatFieldValue(
                        fieldChange.field,
                        fieldChange.newValue
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      ))}
    </div>
  );
};

export default ContractChangesHistory;
